import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, canonicalLanguage } from '../i18n/config';
import { useProfileStore } from '../store/useProfileStore';
import { useUpdateProfileLanguage } from './useProfile';

/**
 * Shared logic for every language-selection control (the dropdown
 * LanguageSwitcher and the header HeaderLanguageButton). Owns the current-code
 * resolution and the optimistic-flip-then-persist select flow so the two UIs
 * can't drift:
 *
 *   1. `i18n.changeLanguage(next)` — UI flips immediately; LanguageDetector
 *      caches the code to localStorage, which is the only persistence for
 *      logged-out guests.
 *   2. Authed only — `PATCH /profile {language}`: persist canonically; revert
 *      the i18n language if the PATCH fails.
 *
 * Returns `current` in canonical case (so `es-mx` from a URL still matches the
 * meta map) and the supported-language list for rendering.
 */
export function useLanguageSelection() {
  const { i18n: rti18n } = useTranslation();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const updateLang = useUpdateProfileLanguage();

  // Prefer i18next's resolved language (matches the loaded dictionary), with a
  // sanity fallback to the profile's stored value for bootstrap-order races.
  const currentRaw =
    rti18n.resolvedLanguage || rti18n.language || activeProfile?.language || 'en';
  const current = canonicalLanguage(currentRaw) ?? 'en';

  const select = async (next: string) => {
    if (next === current) return;

    const previous = current;
    await rti18n.changeLanguage(next);

    // Unauthed: no profile to PATCH — the localStorage cache is the whole
    // persistence story. Authed: persist canonically to the profile.
    if (!activeProfile) return;

    try {
      await updateLang.mutateAsync({ profileId: activeProfile.id, language: next });
    } catch {
      // apiService surfaces a translated error modal; walk the UI back to the
      // language the BE still believes in.
      await rti18n.changeLanguage(previous);
    }
  };

  return {
    current,
    select,
    isPending: updateLang.isPending,
    languages: SUPPORTED_LANGUAGES,
  };
}
